'use client'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import axios from 'axios'
import type { Price } from '@/root/shared-types/dto/SailPricing'
import type { PassengerFormData, SailRefParam } from '@/root/shared-types/selectTicketPage/selectTicket'
import type { BookingStatus, VehicleDetailsState } from '@/root/shared-types/reservation/reservation-types'
import type { Reservation } from '@/root/shared-types'
import type { RequestDTO } from '@/root/shared-providers/BookingCart/types'
import { useBookingCart } from '@/root/shared-providers/BookingCart'
import { Action } from '@/root/shared-providers/BookingCart/reducer'
import { reservationService } from '@/root/shared-api'
import { throwResidentDiscountError } from '@/root/shared-helpers/resident'
import { useCustomRouter, useGetTranslations } from '@/root/shared-hooks'
import { isUserVisibleItem } from '@/root/shared-helpers/reservation/reservation-item-filters'
import { getReservationErrorKey } from '@/root/modules/shared/SelectTicketPage/helpers/getReservationErrorKey/getReservationErrorKey'
import { toCartItemSailParams } from '@/root/modules/shared/SelectTicketPage/helpers/parseSailRefParams/parseSailRefParams'
import {
  addTickets,
  buildEnrichedRequest,
  getApiCode,
  newSlots,
  removeLastTicket,
  removeVehicle,
  truncateTickets,
  type TicketList,
} from '@/root/modules/shared/SelectTicketPage/helpers/ticketRequest/ticketRequest'
import {
  getCartItemsFromReservation,
  getInitialPassengersByCode,
  getInitialQuantityByCode,
  getPassengersFormData,
  getSeqNsByCode,
} from '@/root/modules/shared/SelectTicketPage/helpers/reservationToCart/reservationToCart'

type UseTicketReservationParams = {
  /** Re-reads the sails' capacity after the reservation changes. */
  revalidateInventory: () => void
  sailParams: SailRefParam[]
  /** Code of the first resident category: every resident ticket shows on its card. */
  firstResidentCode?: string
  /** The reservation of `?token`, to continue editing it. */
  reservation?: Reservation
}

/** The reservation behind the ticket cards: every quantity change, passenger save or delete is sent to the backend. */
export const useTicketReservation = ({
  sailParams,
  reservation,
  firstResidentCode,
  revalidateInventory,
}: UseTicketReservationParams) => {
  const {
    dispatch,
    state: { reservationId, currentRequest, reservationToken, nextSeqNum },
  } = useBookingCart()

  const { locale, push } = useCustomRouter()
  const { getTranslation } = useGetTranslations()

  const getErrorMessage = useCallback(
    (error: unknown): string => getTranslation(getReservationErrorKey(getApiCode(error))),
    [getTranslation]
  )

  const [status, setStatus] = useState<BookingStatus>()
  const [reservationError, setReservationError] = useState<string>()
  const [blocked, setBlocked] = useState<boolean>()
  const [passengersFormData, setPassengersFormData] = useState<Record<string, Record<number, PassengerFormData>>>({})
  const [formStates, setFormStates] = useState<Record<string, { saved: number; total: number }>>({})
  const [expireAt, setExpireAt] = useState<string | undefined>(reservation?.expireAt)
  const [reservationNew, setReservation] = useState<Reservation | undefined>(reservation)

  const cartItemSailParams = useMemo(() => toCartItemSailParams(sailParams), [sailParams])

  // Resolves the promise of the passenger save once its request is done
  const pendingSaveResolveRef = useRef<(() => void) | null>(null)

  const handleReservationCreateUpdateModify = useCallback(async () => {
    const enrichedRequest = buildEnrichedRequest(currentRequest, passengersFormData, reservationNew)
    try {
      setBlocked(true)
      setReservationError(undefined)

      if (!reservationId) {
        const reservation = await reservationService.createNewReservation(axios, locale, enrichedRequest)
        dispatch({ type: Action.setReservationId, payload: reservation.reservationId })
        setReservation(reservation)
        if (reservation.expireAt) setExpireAt(reservation.expireAt)
        revalidateInventory()
      } else {
        const reservation = await reservationService.updateReservationTickets(
          axios,
          `${reservationId}`,
          locale,
          enrichedRequest
        )
        setReservation(reservation)
        if (reservation.expireAt) setExpireAt(reservation.expireAt)
        revalidateInventory()
      }
    } catch (error) {
      setReservationError(getErrorMessage(error))
    } finally {
      setBlocked(false)
      const savedResolve = pendingSaveResolveRef.current
      pendingSaveResolveRef.current = null
      savedResolve?.()
    }
  }, [
    currentRequest,
    passengersFormData,
    reservationNew,
    dispatch,
    locale,
    reservationId,
    getErrorMessage,
    revalidateInventory,
  ])

  const handleConfirmReservation = useCallback(async () => {
    try {
      setBlocked(true)
      setReservationError(undefined)

      if (!reservationId) {
        return
      }

      const {
        reservationId: confirmedResId,
        token,
        status: confirmedStatus,
      } = await reservationService.confirm(axios, `${reservationId}`, locale)

      setStatus(confirmedStatus)
      dispatch({ type: Action.setReservationToken, payload: token ?? '' })
      dispatch({ type: Action.setReservationId, payload: confirmedResId })
      push(`/${locale}/confirm/${token}`)
    } catch (error) {
      setReservationError(getErrorMessage(error))
    } finally {
      setBlocked(false)
    }
  }, [dispatch, locale, reservationId, push, getErrorMessage])

  // Order matters: the ref must be refreshed before the save effect below reads it, or the save request
  // goes out with the form data of the previous render
  const latestHandleReservation = useRef(handleReservationCreateUpdateModify)
  useEffect(() => {
    latestHandleReservation.current = handleReservationCreateUpdateModify
  })

  // Auto-trigger after passenger form save
  const pendingPassengerSave = useRef(false)
  useEffect(() => {
    if (!pendingPassengerSave.current) return
    pendingPassengerSave.current = false
    latestHandleReservation.current()
  }, [passengersFormData])

  // Hydrate cart from reservation on first mount
  const reservationInitRef = useRef(false)
  useEffect(() => {
    if (reservationInitRef.current || !reservation) return
    reservationInitRef.current = true

    dispatch({ type: Action.setReservationId, payload: reservation.reservationId })
    if (reservation.token) {
      dispatch({ type: Action.setReservationToken, payload: reservation.token })
    }
    for (const payload of getCartItemsFromReservation(reservation, cartItemSailParams)) {
      dispatch({ type: Action.addToCart, payload })
    }
    // Pre-fill passenger form data (without triggering API call)
    setPassengersFormData(getPassengersFormData(reservation))
  }, [reservation, dispatch, cartItemSailParams])

  const callReservationApi = useCallback(
    async (request: RequestDTO): Promise<Reservation> => {
      const enrichedRequest = buildEnrichedRequest(request, passengersFormData, reservationNew)
      if (!reservationId) {
        return reservationService.createNewReservation(axios, locale, enrichedRequest)
      }
      return reservationService.updateReservationTickets(axios, `${reservationId}`, locale, enrichedRequest)
    },
    [reservationId, locale, passengersFormData, reservationNew]
  )

  // Rebuilds the cart from the backend's answer, so seqNs and quantities follow the server
  const syncFromApiResponse = useCallback(
    (reservation: Reservation) => {
      dispatch({ type: Action.clearCart })
      dispatch({ type: Action.setReservationId, payload: reservation.reservationId })
      if (reservation.token) {
        dispatch({ type: Action.setReservationToken, payload: reservation.token })
        const url = new URL(window.location.href)
        if (url.searchParams.get('token') !== reservation.token) {
          url.searchParams.set('token', reservation.token)
          window.history.replaceState({}, '', url.toString())
        }
      }
      for (const payload of getCartItemsFromReservation(reservation, cartItemSailParams)) {
        dispatch({ type: Action.addToCart, payload })
      }

      setReservation(reservation)
      if (reservation.expireAt) setExpireAt(reservation.expireAt)
      revalidateInventory()
    },
    [dispatch, cartItemSailParams, revalidateInventory]
  )

  // Sends a changed ticket list; a failure shows the banner and rethrows so the card reverts its quantity
  const sendTickets = useCallback(
    async (request: RequestDTO): Promise<void> => {
      try {
        const reservation = await callReservationApi(request)
        syncFromApiResponse(reservation)
      } catch (error) {
        setReservationError(getErrorMessage(error))
        throw error
      } finally {
        setBlocked(false)
      }
    },
    [callReservationApi, syncFromApiResponse, getErrorMessage]
  )

  const startRequest = useCallback(() => {
    setBlocked(true)
    setReservationError(undefined)
  }, [])

  const incTicket = useCallback(
    async (list: TicketList, price: Price): Promise<void> => {
      startRequest()
      const slots = newSlots(1, nextSeqNum)
      await sendTickets(addTickets(currentRequest, list, price.priceCategory.code, slots, cartItemSailParams))
    },
    [currentRequest, nextSeqNum, cartItemSailParams, sendTickets, startRequest]
  )

  const decTicket = useCallback(
    async (list: TicketList, price: Price): Promise<void> => {
      startRequest()
      const request = removeLastTicket(currentRequest, list, price.priceCategory.code)
      if (!request) {
        setBlocked(false)
        return
      }
      await sendTickets(request)
    },
    [currentRequest, sendTickets, startRequest]
  )

  // Sets the exact quantity typed into a card in one request
  const setTicketQuantity = useCallback(
    async (list: TicketList, price: Price, targetQty: number): Promise<void> => {
      const code = price.priceCategory.code
      const currentQty = currentRequest[list]?.find((ticket) => ticket.priceCategory === code)?.quantity ?? 0
      const delta = targetQty - currentQty
      if (delta === 0) return

      startRequest()
      await sendTickets(
        delta > 0
          ? addTickets(currentRequest, list, code, newSlots(delta, nextSeqNum), cartItemSailParams)
          : truncateTickets(currentRequest, list, code, targetQty)
      )
    },
    [currentRequest, nextSeqNum, cartItemSailParams, sendTickets, startRequest]
  )

  const handleInc = useCallback((price: Price) => incTicket('passengerTickets', price), [incTicket])
  const handleDec = useCallback((price: Price) => decTicket('passengerTickets', price), [decTicket])
  const handleSetPassengerQuantity = useCallback(
    (price: Price, qty: number) => setTicketQuantity('passengerTickets', price, qty),
    [setTicketQuantity]
  )
  // Car deck tickets (bicycles); vehicle details are filled in later when the user submits them
  const handleVehicleInc = useCallback((price: Price) => incTicket('vehicleTickets', price), [incTicket])
  const handleVehicleDec = useCallback((price: Price) => decTicket('vehicleTickets', price), [decTicket])
  const handleSetVehicleQuantity = useCallback(
    (price: Price, qty: number) => setTicketQuantity('vehicleTickets', price, qty),
    [setTicketQuantity]
  )

  // Car-deck vehicles (cars, trailers) with their plate and dimensions, so the backend takes their space on the car
  // deck. The trial ticket page uses them; Kihnu will too when it sells vehicles on the purchase flow.
  // With `replaceSeqN` the new details take the place of that booked vehicle in one request.
  const handleVehicleAdd = useCallback(
    async (price: Price, vehicle: VehicleDetailsState, replaceSeqN?: number): Promise<void> => {
      startRequest()
      await sendTickets(
        addTickets(
          replaceSeqN === undefined ? currentRequest : removeVehicle(currentRequest, replaceSeqN),
          'vehicleTickets',
          price.priceCategory.code,
          [{ seqN: nextSeqNum + 1, ...vehicle }],
          cartItemSailParams
        )
      )
    },
    [currentRequest, nextSeqNum, cartItemSailParams, sendTickets, startRequest]
  )
  const handleVehicleRemove = useCallback(
    async (seqN: number): Promise<void> => {
      startRequest()
      await sendTickets(removeVehicle(currentRequest, seqN))
    },
    [currentRequest, sendTickets, startRequest]
  )

  // Resident passenger handlers.
  // Inc/Dec are UI-only: no API call is made when clicking +/-.
  // The reservation entry is created when the personal ID check succeeds (handleResidentIdVerified).
  const handleResidentInc = useCallback(async () => {}, [])
  const handleResidentDec = useCallback(async () => {}, [])

  const handleResidentIdVerified = useCallback(
    async (price: Price, slotIndex: number, personalId: string, verifiedPriceCategory: string): Promise<number> => {
      startRequest()

      const newResidentEntry = {
        quantity: 1,
        priceCategory: verifiedPriceCategory,
        sailPackages: cartItemSailParams,
        passengers: [
          {
            seqN: nextSeqNum + 1,
            ...(personalId && { personalIdentificationNumber: personalId }),
          },
        ],
      }

      try {
        const reservation = await callReservationApi({
          ...currentRequest,
          passengerTickets: [...(currentRequest.passengerTickets ?? []), newResidentEntry],
        })
        syncFromApiResponse(reservation)
        const newGuest = reservation.guests?.find((guest) => guest.personalIdentificationNumber === personalId)
        return newGuest?.seqN ?? 0
      } catch (error) {
        throwResidentDiscountError(getTranslation, getApiCode(error))
        setReservationError(getErrorMessage(error))
        throw error
      } finally {
        setBlocked(false)
      }
    },
    [
      currentRequest,
      nextSeqNum,
      cartItemSailParams,
      callReservationApi,
      syncFromApiResponse,
      getTranslation,
      getErrorMessage,
      startRequest,
    ]
  )

  const handlePassengerDeleted = useCallback(
    async (price: Price, slotIndex: number, verifiedPriceCategory?: string, seqN?: number): Promise<void> => {
      const priceCode = price.priceCategory.code
      const formDataKey = seqN ?? slotIndex

      const removeFromFormData = () => {
        setPassengersFormData((prev) => {
          const next = { ...prev }

          // Remove by seqN (or slotIndex fallback) from the original priceCode key
          if (next[priceCode]?.[formDataKey] !== undefined) {
            const { [formDataKey]: _, ...rest } = next[priceCode]
            next[priceCode] = rest
          }

          // Also remove from verified priceCategory key if different
          if (
            verifiedPriceCategory &&
            verifiedPriceCategory !== priceCode &&
            next[verifiedPriceCategory]?.[formDataKey] !== undefined
          ) {
            const { [formDataKey]: _, ...rest } = next[verifiedPriceCategory]
            next[verifiedPriceCategory] = rest
          }

          return next
        })
      }

      // Unconfirmed resident: no seqN means not yet in the reservation.
      // No API call needed — just clean up the form data.
      if (price.priceCategory.isResident && !seqN) {
        removeFromFormData()
        return
      }

      // Confirmed passenger (resident or non-resident): filter by seqN.
      // seqN comes from PriceCard (synced via seqNs prop) or fallback from currentRequest.
      const targetSeqN =
        seqN ??
        (currentRequest.passengerTickets ?? []).find((ticket) => ticket.priceCategory === priceCode)?.passengers[
          slotIndex
        ]?.seqN

      if (targetSeqN == null) {
        removeFromFormData()
        return
      }

      const entryIdx = (currentRequest.passengerTickets ?? []).findIndex((ticket) =>
        ticket.passengers.some((passenger) => passenger.seqN === targetSeqN)
      )
      if (entryIdx === -1) {
        removeFromFormData()
        return
      }

      startRequest()

      const existing = currentRequest.passengerTickets![entryIdx]
      const updatedTickets =
        existing.quantity > 1
          ? currentRequest.passengerTickets!.map((ticket, index) =>
              index === entryIdx
                ? {
                    ...ticket,
                    quantity: ticket.quantity - 1,
                    passengers: ticket.passengers.filter((passenger) => passenger.seqN !== targetSeqN),
                  }
                : ticket
            )
          : currentRequest.passengerTickets!.filter((_, index) => index !== entryIdx)

      try {
        const updatedReservation = await callReservationApi({
          ...currentRequest,
          passengerTickets: updatedTickets,
        })
        syncFromApiResponse(updatedReservation)
        removeFromFormData()
      } catch (error) {
        setReservationError(getErrorMessage(error))
      } finally {
        setBlocked(false)
      }
    },
    [currentRequest, callReservationApi, syncFromApiResponse, getErrorMessage, startRequest]
  )

  const handlePassengerSaved = useCallback(
    (priceCode: string, slotIndex: number, data: PassengerFormData): Promise<void> => {
      return new Promise<void>((resolve) => {
        pendingSaveResolveRef.current = resolve
        setPassengersFormData((prev) => ({
          ...prev,
          [priceCode]: { ...prev[priceCode], [slotIndex]: data },
        }))
        pendingPassengerSave.current = true
      })
    },
    []
  )

  const handleFormStateChange = useCallback((priceCode: string, saved: number, total: number) => {
    setFormStates((prev) => ({ ...prev, [priceCode]: { saved, total } }))
  }, [])

  const handleCheckout = useCallback(async () => {
    if (status === 'OFFER' && reservationToken) {
      push(`/${locale}/confirm/${reservationToken}`)
      return
    }
    await handleConfirmReservation()
  }, [status, reservationToken, handleConfirmReservation, push, locale])

  const allFormsSaved = Object.values(formStates).every(({ saved, total }) => total === 0 || saved === total)
  const hasCartItems = (reservationNew?.items ?? []).some(isUserVisibleItem)

  const seqNsByCode = useMemo(
    () => getSeqNsByCode(reservationNew?.items, firstResidentCode),
    [reservationNew?.items, firstResidentCode]
  )
  const initialQuantityByCode = useMemo(
    () => getInitialQuantityByCode(reservation, firstResidentCode),
    [reservation, firstResidentCode]
  )
  const initialPassengersByCode = useMemo(
    () => getInitialPassengersByCode(reservation, firstResidentCode),
    [reservation, firstResidentCode]
  )

  return {
    reservation: reservationNew,
    currentRequest,
    reservationError,
    getErrorMessage,
    clearReservationError: () => setReservationError(''),
    blocked: !!blocked,
    expireAt,
    nextDisabled: !reservationId || !allFormsSaved || !!blocked || !hasCartItems,
    seqNsByCode,
    initialQuantityByCode,
    initialPassengersByCode,
    handleInc,
    handleDec,
    handleSetPassengerQuantity,
    handleVehicleInc,
    handleVehicleDec,
    handleSetVehicleQuantity,
    handleVehicleAdd,
    handleVehicleRemove,
    handleResidentInc,
    handleResidentDec,
    handleResidentIdVerified,
    handlePassengerSaved,
    handlePassengerDeleted,
    handleFormStateChange,
    handleCheckout,
  }
}
